"use client"

import { Plus } from "lucide-react"
import { observer } from "mobx-react-lite"

import type { GroupMemberDraft } from "@/store/appointment-store"
import { cn } from "@/lib/utils"
import { useStore } from "@/app/components/common/store.provider"

export function guestLabel(member: GroupMemberDraft, index: number): string {
  const name = member.beneficiaryName.trim()
  if (name) return name
  return `Guest ${index + 1}`
}

function formatTimeDisplay(time: string): string {
  const match = time.match(/^(\d{1,2}):(\d{2})/)
  if (!match) return time
  const h = parseInt(match[1], 10)
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${match[2]} ${h < 12 ? "AM" : "PM"}`
}

type GroupGuestTabsProps = {
  /** Which per-guest progress hint to show under each name. */
  detail: "services" | "time"
}

/**
 * Guest switcher shown above the shared services/time steps so the same
 * single-booking UI can be driven one guest at a time.
 */
export const GroupGuestTabs = observer(function GroupGuestTabs({
  detail,
}: GroupGuestTabsProps) {
  const { appointmentStore } = useStore()
  const members = appointmentStore.groupMembers
  const activeId = appointmentStore.activeMember?.localId
  const canAdd = members.length < appointmentStore.maxGroupBookingSize

  if (members.length === 0) return null

  return (
    <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide py-1">
      {members.map((member, index) => {
        const isActive = member.localId === activeId
        const hint =
          detail === "services"
            ? member.services.length > 0
              ? `${member.services.length} service${member.services.length === 1 ? "" : "s"}`
              : "No services"
            : member.startTime
              ? formatTimeDisplay(member.startTime)
              : "No time"
        const isComplete =
          detail === "services" ? member.services.length > 0 : Boolean(member.startTime)

        return (
          <button
            key={member.localId}
            type="button"
            onClick={() => appointmentStore.setActiveGroupMember(member.localId)}
            className={cn(
              "flex min-h-11 shrink-0 flex-col items-start rounded-full border px-4 py-1.5 text-left transition-colors",
              isActive
                ? "border-primary bg-primary/5"
                : "border-border hover:bg-muted",
            )}
          >
            <span className="max-w-32 truncate text-sm font-medium text-foreground">
              {guestLabel(member, index)}
            </span>
            <span
              className={cn(
                "text-[11px]",
                isComplete ? "text-muted-foreground" : "text-destructive",
              )}
            >
              {hint}
            </span>
          </button>
        )
      })}
      {canAdd ? (
        <button
          type="button"
          onClick={() => appointmentStore.addGroupMember()}
          className="flex min-h-11 shrink-0 items-center gap-1 rounded-full border border-dashed border-border px-4 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Plus className="h-4 w-4" />
          Add guest
        </button>
      ) : null}
    </div>
  )
})
